import { getIncomingRenewalColumns } from '@components/charts/membership/analytics-overview/incomingRenewalColumns'
import { useAnalytics } from '@contexts/AnalyticsContext'
import { MembershipIncomingRenewal } from '@models/membership/MembershipIncomingRenewal'
import { fetchMembershipIncomingRenewals } from '@services/membership/MembershipIncomingRenewalService'
import { initialQuery, SBTable } from '@supplement-bacon/sb-table-filters'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'

const PREVIEW_ROW_COUNT = 5

/**
 * Compact "Next renewals to come" preview for the Revenue page: the first N
 * upcoming renewals from today, whatever their date (the API bounds the range
 * to today + 365 days when no end date is sent), no pagination or
 * filters, no row actions - the detail lives on `IncomingRenewalsPage`,
 * reachable through the section's "View all renewals" link. It owns its rows
 * (local state, newest request wins) instead of sharing the drill-down's table
 * state.
 */
const IncomingRenewalTable = () => {
  const [rows, setRows] = useState<MembershipIncomingRenewal[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const latest = useRef(0)

  const { selectedMembershipPrograms, isMembershipProgramReady } =
    useAnalytics()
  const programId = selectedMembershipPrograms?.[0]

  useEffect(() => {
    // Scoped to the page's program, like every block of the Revenue page.
    if (!isMembershipProgramReady) return

    const startDate = dayjs().format('YYYY-MM-DD')

    const requestId = ++latest.current
    setIsLoading(true)

    fetchMembershipIncomingRenewals({
      ...initialQuery,
      columnKey: 'date',
      order: 'ascend',
      pagination: { current: 1, pageSize: PREVIEW_ROW_COUNT },
      filters: {
        startDate: [startDate],
        // Only what is still to come: not the renewals already settled.
        outcome: ['upcoming'],
        ...(programId !== undefined && { programs: [String(programId)] }),
      },
    })
      .then(({ data }) => {
        if (requestId !== latest.current) return
        setRows(data)
      })
      .catch((error) => {
        if (requestId !== latest.current) return
        console.error('Error fetching the next renewals:', error)
        setRows([])
      })
      .finally(() => {
        if (requestId === latest.current) setIsLoading(false)
      })
  }, [programId, isMembershipProgramReady])

  return (
    <SBTable
      columns={getIncomingRenewalColumns({ compact: true })}
      dataSource={rows}
      loading={isLoading}
      empty={{ description: 'No renewals found' }}
      pagination={false}
      tableLayout="fixed"
      rowKey="id"
    />
  )
}

export default IncomingRenewalTable
